'use client'

import { Event, Team, RoundDetails } from '@/types'
import { Tabs, Select, TabsRef, Button } from 'flowbite-react'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import EventDetail from '@/app/web/events/[slug]/ui/EventDetail'
import MentorAndJudge from '@/app/web/events/[slug]/ui/MentorAndJudge'
import Link from 'next/link'
import { Session } from 'next-auth'
import { TeamAndSubmission } from '../TeamAndSubmission'
import Participants from '@/app/web/events/[slug]/ui/Participants'
import { useUserInfo } from '@/app/web/context/UserInfoContext'
import { useSearchParams } from 'next/navigation'
import { detailTabsTheme } from '@/components/event-detail'

interface TabContentsProps {
  event: Event
  rounds: RoundDetails[]
  session?: Session
  team?: Team
}

export interface TabContentsHandle {
  setActiveTab: (index: number) => void
}

export const TabContents = forwardRef<TabContentsHandle, TabContentsProps>(
  ({ event, rounds, session, team }, ref) => {
    const searchParams = useSearchParams()
    const [activeTab, setActiveTab] = useState(0)
    const tabWrapperRef = useRef<HTMLDivElement>(null)
    const [hasScrolled, setHasScrolled] = useState(false)

    const tabsRef = useRef<TabsRef>(null)
    const { userInfo } = useUserInfo()

    const isParticipated = userInfo
      ? userInfo.participations?.some((item) => item.event_id === event.id)
      : false

    const isCustomized = event.customized
    const isAdmin = userInfo ? userInfo.roles?.includes('admin') : false

    const tabList = [
      { key: 'detail', label: 'Details', active: true },
      { key: 'checklist', label: 'Checklist', active: isParticipated },
      { key: 'team', label: 'Team & Submission', active: isParticipated },
      {
        key: 'participants',
        label: 'Participants',
        active: isAdmin || isParticipated,
      },
      ...(event.show_mentors || event.show_judges
        ? [{ key: 'mentors', label: 'Mentors & Judges', active: true }]
        : []),
      ...(event.gallery
        ? [{ key: 'gallery', label: 'Gallery', active: true }]
        : []),
      { key: 'faq', label: 'FAQ', active: true },
    ]

    const tabKeyToIndex = tabList.reduce<Record<string, number>>(
      (acc, tab, index) => {
        acc[tab.key] = index
        return acc
      },
      {}
    )

    useImperativeHandle(ref, () => ({
      setActiveTab: (index: number) => {
        tabsRef.current?.setActiveTab(index)
      },
    }))

    useEffect(() => {
      const tab = searchParams?.get('tab')
      if (tab) {
        if (tabKeyToIndex[tab] && tabList?.[tabKeyToIndex[tab]]?.active) {
          tabsRef.current?.setActiveTab(tabKeyToIndex[tab])
        } else {
          tabsRef.current?.setActiveTab(0)
        }
      }
    }, [searchParams])

    useEffect(() => {
      if (searchParams?.get('tab') && !hasScrolled && tabWrapperRef.current) {
        const rect = tabWrapperRef.current.getBoundingClientRect()
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          tabWrapperRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
          setHasScrolled(true)
        }
      }
    }, [hasScrolled])

    const handleTabChange = (index: number) => {
      setActiveTab(index)
      const tabKey = Object.keys(tabKeyToIndex).find(
        (key) => tabKeyToIndex[key] === index
      )
      if (tabKey) {
        const url = new URL(window.location.href)
        url.searchParams.set('tab', tabKey)
        window.history.pushState({}, '', url.toString())
      }
    }

    return (
      <div ref={tabWrapperRef}>
        <Select
          className="lg:hidden sticky top-16 z-20"
          onChange={(e) =>
            tabsRef.current?.setActiveTab(tabKeyToIndex[e.target.value])
          }
        >
          {tabList.map((item, index) => (
            <option key={index} disabled={!item.active} value={item.key}>
              {item.label}
            </option>
          ))}
        </Select>
        <Tabs
          theme={detailTabsTheme}
          ref={tabsRef}
          onActiveTabChange={(index) => {
            handleTabChange(index)
          }}
          variant="underline"
        >
          <Tabs.Item active title={<span tab-key="details">Details</span>}>
            <EventDetail event={event} />
          </Tabs.Item>
          <Tabs.Item
            disabled={!isParticipated}
            title={<span tab-key="checklist">Checklist</span>}
          >
            {event.contents?.checklist ? (
              <div className="ql-snow w-full max-w-full break-words">
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{
                    __html: event.contents?.checklist || '',
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col gap-6 w-full py-12">
                <div className="mx-auto flex flex-col gap-2 text-center max-w-xl">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Hacker&apos;s checklist coming soon – hang tight! 👀
                  </h3>
                  <p className="text-gray-500">
                    We&apos;re working hard to prepare a checklist that will
                    help you stay organized and focused throughout your
                    hackathon journey. In the meantime, feel free to explore
                    more hackathons!
                  </p>
                </div>
                <Button
                  color="dark"
                  className="mx-auto px-20 py-2 inline-block font-medium"
                  as={Link}
                  href={'/web/events'}
                >
                  Explore Hackathons
                </Button>
              </div>
            )}
          </Tabs.Item>
          <Tabs.Item
            disabled={!isParticipated}
            title={<span tab-key="team">Team & Submission</span>}
          >
            {session && isParticipated && event.slug && (
              <TeamAndSubmission
                eventSlug={event.slug}
                session={session}
                team={team}
                rounds={rounds}
                themes={event.challenge_themes}
              />
            )}
          </Tabs.Item>

          <Tabs.Item
            disabled={!isAdmin && !isParticipated}
            title={<span tab-key="participants">Participants</span>}
          >
            <Participants event={event} />
          </Tabs.Item>

          {(event.show_mentors || event.show_judges) && (
            <Tabs.Item title="Mentors & Judges">
              <MentorAndJudge event={event} />
            </Tabs.Item>
          )}
          {event.gallery && (
            <Tabs.Item title={<span tab-key="gallery">Gallery</span>}>
              <div
                className="w-full h-[90vh] p-4"
                dangerouslySetInnerHTML={{ __html: event?.gallery }}
              ></div>
            </Tabs.Item>
          )}
          <Tabs.Item title={<span tab-key="faq">FAQ</span>}>
            <div className="w-full py-14 flex flex-col items-start gap-6">
              {event.contents?.faq ? (
                <div className="ql-snow w-full max-w-full break-words">
                  <div
                    className="ql-editor"
                    dangerouslySetInnerHTML={{
                      __html: event.contents.faq,
                    }}
                  />
                </div>
              ) : (
                <div className="my-5 flex flex-col w-full h-full items-center text-center">
                  <span className="text-gray-900 text-lg font-semibold leading-10">
                    FAQ coming soon – hang tight! 👀
                  </span>
                  <p className="text-gray-500 max-w-xl">
                    We&apos;re working hard to ensure that we&apos;ll have the
                    answers to any questions you might have. In the meantime,
                    feel free to explore more hackathons!
                  </p>
                </div>
              )}
            </div>
          </Tabs.Item>
        </Tabs>
      </div>
    )
  }
)

TabContents.displayName = 'TabContents'
